import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, RefreshCw } from 'lucide-react';
import { isDespiaApp } from '@/utils/despia';

const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isPreviewHost =
  typeof window !== 'undefined' &&
  (window.location.hostname.includes('lovableproject.com') ||
    window.location.hostname.includes('id-preview--'));

const PWA_DISABLED =
  import.meta.env.DEV || isInIframe || isPreviewHost || isDespiaApp();

const LEGACY_SW_PATHS = ['/sw.js', '/service-worker.js', '/pwa-sw.js'];

const REFRESH_ATTEMPT_KEY = 'pwa:refresh-attempted-build';
const DISMISSED_KEY = 'pwa:dismissed-build';
// Sticky "we just tried to refresh" marker. Independent of build-id matching
// so we can suppress re-prompts even when latestBuildRef was empty at click
// time, or when /version.json reports a different newer id on the next load.
const REFRESH_COOLDOWN_KEY = 'pwa:refresh-cooldown-until';
const REFRESH_FROM_BUILD_KEY = 'pwa:refresh-from-build';
// How long to suppress the prompt after the user clicks Refresh, in ms.
// Long enough to absorb CDN propagation + a couple of stale reloads, short
// enough that a genuinely new build later in the day still surfaces.
const REFRESH_COOLDOWN_MS = 10 * 60 * 1000;

type WindowWithUpdateCheck = Window & {
  __checkForAppUpdate?: () => Promise<void>;
};

// Build id baked in at compile time (see vite.config.ts -> define).
const CURRENT_BUILD_ID: string =
  typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : '';

function compareBuildIds(a: string | null | undefined, b: string | null | undefined) {
  const normalizedA = (a ?? '').replace(/\D/g, '');
  const normalizedB = (b ?? '').replace(/\D/g, '');

  if (!normalizedA && !normalizedB) return 0;
  if (!normalizedA) return -1;
  if (!normalizedB) return 1;
  if (normalizedA === normalizedB) return 0;

  return normalizedA > normalizedB ? 1 : -1;
}

function stripTransientUpdateParams() {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  let changed = false;

  ['v', 'sw-reset', 'force-refresh'].forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });

  if (changed) {
    window.history.replaceState({}, '', url.toString());
  }
}

// Fetch the deployed build id from /version.json. This endpoint is emitted
// by the build (emitVersionJsonPlugin) and is intentionally tiny + not
// referenced by the SW runtimeCaching rules, so it always hits the network.
async function fetchLatestBuildId(): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
      headers: {
        'cache-control': 'no-cache, no-store, max-age=0',
        pragma: 'no-cache',
      },
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { build?: unknown };
    return typeof data.build === 'string' ? data.build : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function isLegacyRegistration(registration: ServiceWorkerRegistration) {
  const scriptUrls = [
    registration.active?.scriptURL,
    registration.waiting?.scriptURL,
    registration.installing?.scriptURL,
  ].filter((url): url is string => Boolean(url));

  return scriptUrls.some((url) => LEGACY_SW_PATHS.some((path) => url.includes(path)));
}

async function cleanupLegacyServiceWorkers() {
  if (!(typeof window !== 'undefined' && 'serviceWorker' in navigator)) {
    return false;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  const legacyRegistrations = registrations.filter(isLegacyRegistration);

  if (!legacyRegistrations.length) {
    return false;
  }

  await Promise.all(
    legacyRegistrations.map(async (registration) => {
      try {
        await registration.unregister();
      } catch (error) {
        console.warn('[PWA] Failed to unregister legacy service worker:', error);
      }
    }),
  );

  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
  }

  return true;
}

// Aggressive cache-bypass used by the "Refresh now" path. Deletes every
// Cache Storage bucket owned by this origin and unregisters the app's own
// service-worker registrations so the next navigation is forced to fetch
// /index.html and the JS bundle from the network rather than the SW cache.
// We intentionally leave non-app workers (Firebase Messaging / OneSignal)
// alone, they live at different script URLs.
async function purgeAppShellCachesAndWorkers() {
  if (typeof window === 'undefined') return;

  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.allSettled(cacheNames.map((name) => caches.delete(name)));
    } catch (error) {
      console.warn('[PWA] Failed to clear caches before refresh:', error);
    }
  }

  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map(async (registration) => {
          const scriptUrls = [
            registration.active?.scriptURL,
            registration.waiting?.scriptURL,
            registration.installing?.scriptURL,
          ].filter((url): url is string => Boolean(url));
          // Only touch the app-shell worker; leave messaging workers alone.
          const isAppShell = scriptUrls.some(
            (url) =>
              url.includes('/app-sw.js') ||
              LEGACY_SW_PATHS.some((path) => url.includes(path)),
          );
          if (!isAppShell) return;
          try {
            await registration.unregister();
          } catch (error) {
            console.warn('[PWA] Failed to unregister app worker before refresh:', error);
          }
        }),
      );
    } catch (error) {
      console.warn('[PWA] Failed to enumerate service workers before refresh:', error);
    }
  }
}

export function PWAUpdatePrompt() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const reloadingRef = useRef(false);
  const updateIntervalRef = useRef<number | undefined>(undefined);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const latestBuildRef = useRef<string | null>(null);

  const readStored = (key: string): string | null => {
    try { return window.localStorage.getItem(key); } catch { return null; }
  };
  const writeStored = (key: string, value: string) => {
    try { window.localStorage.setItem(key, value); } catch {}
  };
  const clearStored = (key: string) => {
    try { window.localStorage.removeItem(key); } catch {}
  };

  const refreshRegistration = async () => {
    if (!(typeof window !== 'undefined' && 'serviceWorker' in navigator)) {
      return registrationRef.current;
    }

    try {
      const registration = (await navigator.serviceWorker.getRegistration('/')) ?? registrationRef.current;
      registrationRef.current = registration ?? null;
      return registration ?? null;
    } catch {
      return registrationRef.current;
    }
  };

  const reloadToLatest = async () => {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    window.location.reload();
  };

  const promptedRef = useRef(false);

  const promptUserToUpdate = (latestBuild?: string | null) => {
    if (promptedRef.current || reloadingRef.current) return;

    const target = latestBuild ?? latestBuildRef.current;

    // Sticky cooldown: scoped per build id. Only suppress when the latest
    // build matches (or is older than) the one we already tried to refresh
    // to. A genuinely newer build must still surface a prompt, otherwise
    // a stale cooldown from an earlier refresh hides real updates (most
    // visible on mobile, where the prompt may never have appeared yet).
    const cooldownUntil = Number(readStored(REFRESH_COOLDOWN_KEY) ?? '0');
    if (cooldownUntil && Date.now() < cooldownUntil) {
      const cooldownBuild = readStored(REFRESH_ATTEMPT_KEY);
      if (!target || !cooldownBuild) return;
      if (compareBuildIds(target, cooldownBuild) <= 0) return;
      // target is newer than the build we cooled down for, fall through
      // and prompt. Clear the stale cooldown so it can't suppress again.
      clearStored(REFRESH_COOLDOWN_KEY);
      clearStored(REFRESH_FROM_BUILD_KEY);
    }

    if (target) {
      // Don't re-prompt for a build the user already dismissed…
      if (readStored(DISMISSED_KEY) === target) return;
      // …or one we already tried to refresh to. If the reload still
      // landed on the same stale shell (CDN/browser HTML cache), we'd
      // otherwise loop the dialog on every refresh.
      if (readStored(REFRESH_ATTEMPT_KEY) === target) return;
    }

    promptedRef.current = true;
    setUpdateAvailable(true);
  };

  const dismissUpdate = () => {
    const target = latestBuildRef.current;
    if (target) writeStored(DISMISSED_KEY, target);
    setUpdateAvailable(false);
  };

  const applyUpdate = () => {
    if (refreshing) return;
    setRefreshing(true);
    // Remember the build we're trying to upgrade to so we don't
    // immediately re-prompt for the same mismatch after reload.
    const target = latestBuildRef.current;
    if (target) writeStored(REFRESH_ATTEMPT_KEY, target);
    // Sticky cooldown + snapshot of the build we were running, so we can
    // tell on next mount whether the reload actually changed shells.
    writeStored(REFRESH_COOLDOWN_KEY, String(Date.now() + REFRESH_COOLDOWN_MS));
    if (CURRENT_BUILD_ID) writeStored(REFRESH_FROM_BUILD_KEY, CURRENT_BUILD_ID);
    void (async () => {
      let registration = registrationRef.current;

      if ('serviceWorker' in navigator) {
        try {
          await registration?.update();
        } catch {}

        try {
          registration = (await navigator.serviceWorker.getRegistration('/')) ?? registration;
          registrationRef.current = registration ?? null;
        } catch {}
      }

      reloadingRef.current = true;
      const waiting = registration?.waiting;

      if (waiting && 'serviceWorker' in navigator) {
        try {
          waiting.postMessage({ type: 'SKIP_WAITING' });
        } catch {
          await hardReload();
          return;
        }

        window.setTimeout(() => {
          void hardReload();
        }, 250);
        return;
      }

      await hardReload();
    })();
  };

  // Force the browser to bypass the service worker + HTTP cache for the
  // next navigation. We purge app-shell caches, unregister the app SW, and
  // reload to a cache-busted URL so the freshest index.html + JS bundle
  // are fetched from the network.
  const hardReload = async () => {
    try {
      await purgeAppShellCachesAndWorkers();
    } catch {}
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('v', String(Date.now()));
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  };

  useEffect(() => {
    stripTransientUpdateParams();

    if (PWA_DISABLED) return;

    const previousAttempt = readStored(REFRESH_ATTEMPT_KEY);
    if (compareBuildIds(CURRENT_BUILD_ID, previousAttempt) >= 0) {
      clearStored(REFRESH_ATTEMPT_KEY);
      clearStored(DISMISSED_KEY);
    }

    const previousDismissal = readStored(DISMISSED_KEY);
    if (compareBuildIds(CURRENT_BUILD_ID, previousDismissal) >= 0) {
      clearStored(DISMISSED_KEY);
    }

    // Sticky-cooldown housekeeping. Let it end only by time.
    // If we clear it as soon as CURRENT_BUILD_ID advances, a rollout that
    // serves a different newer build on the next reload can immediately
    // re-open the dialog and trap the user in a refresh loop.
    const cooldownUntil = Number(readStored(REFRESH_COOLDOWN_KEY) ?? '0');
    if (cooldownUntil && Date.now() >= cooldownUntil) {
      clearStored(REFRESH_COOLDOWN_KEY);
      clearStored(REFRESH_FROM_BUILD_KEY);
    }

    let cancelled = false;
    let shellCheckInterval: number | undefined;

    const handleControllerChange = () => {
      if (reloadingRef.current) return;

      void (async () => {
        await refreshRegistration();
        const latestBuild = await fetchLatestBuildId();
        if (cancelled || compareBuildIds(latestBuild, CURRENT_BUILD_ID) <= 0) return;

        latestBuildRef.current = latestBuild;
        promptUserToUpdate(latestBuild);
      })();
    };

    const checkForUpdatedShell = async () => {
      if (cancelled || reloadingRef.current) return false;

      try {
        const latestBuild = await fetchLatestBuildId();
        if (cancelled || !latestBuild || !CURRENT_BUILD_ID) return false;

        const buildComparison = compareBuildIds(latestBuild, CURRENT_BUILD_ID);

        if (buildComparison > 0) {
          latestBuildRef.current = latestBuild;

          // Prompt purely on build-ID mismatch. Don't wait for a waiting
          // service worker, on hosts where the SW serves NetworkOnly for
          // navigations the "waiting" state may never appear, leaving
          // users stuck on the old build with no prompt.
          void refreshRegistration();
          promptUserToUpdate(latestBuild);
          return true;
        }

        latestBuildRef.current = null;

        // Latest matches current, or the endpoint briefly reported an older
        // build during rollout. In both cases, don't prompt again.
        if (buildComparison <= 0) {
          clearStored(REFRESH_ATTEMPT_KEY);
          clearStored(DISMISSED_KEY);
        }
      } catch (error) {
        console.warn('[PWA] Version check failed:', error);
      }

      return false;
    };

    const registerServiceWorker = async () => {
      if (!('serviceWorker' in navigator)) return;

      try {
        const registration = await navigator.serviceWorker.register('/app-sw.js', {
          scope: '/',
          updateViaCache: 'none',
        });

        if (cancelled) return;

        registrationRef.current = registration;

        if (registration.waiting && navigator.serviceWorker.controller) {
          void checkForUpdatedShell();
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;

          installing.addEventListener('statechange', () => {
            if (cancelled) return;
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              void checkForUpdatedShell();
            }
          });
        });

        if (updateIntervalRef.current) {
          window.clearInterval(updateIntervalRef.current);
        }

        updateIntervalRef.current = window.setInterval(() => {
          registration.update().catch(() => {});
        }, 60_000);
      } catch (err) {
        console.warn('[PWA] Service worker registration failed:', err);
      }
    };

    navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);

    (async () => {
      try {
        const removedLegacyWorkers = await cleanupLegacyServiceWorkers();
        if (cancelled) return;

        if (removedLegacyWorkers) {
          window.location.reload();
          return;
        }

        await registerServiceWorker();
        if (cancelled) return;

        await checkForUpdatedShell();
        if (cancelled) return;

        shellCheckInterval = window.setInterval(() => {
          void checkForUpdatedShell();
        }, 60_000);

        (window as WindowWithUpdateCheck).__checkForAppUpdate = async () => {
          try {
            await registrationRef.current?.update();
          } catch {}

          const shellChanged = await checkForUpdatedShell();

          if (!shellChanged) {
            toast('You are on the latest version', { duration: 2500 });
          }
        };
      } catch (err) {
        console.warn('[PWA] Register skipped:', err);
      }
    })();

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      void registrationRef.current?.update();
      void checkForUpdatedShell();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
      if (shellCheckInterval) {
        window.clearInterval(shellCheckInterval);
      }
      if (updateIntervalRef.current) {
        window.clearInterval(updateIntervalRef.current);
        updateIntervalRef.current = undefined;
      }
      delete (window as WindowWithUpdateCheck).__checkForAppUpdate;
    };
  }, []);

  return (
    <Dialog open={updateAvailable}>
      <DialogContent
        className="w-[calc(100vw-2rem)] min-w-0 max-w-[calc(100vw-2rem)] overflow-hidden [&>button]:hidden sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">A new version is available</DialogTitle>
          <DialogDescription className="text-center">
            Refresh to get the latest updates and improvements. This only takes a moment.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center gap-2">
          <Button
            onClick={dismissUpdate}
            disabled={refreshing}
            variant="ghost"
            className="w-full sm:w-auto"
            size="lg"
          >
            Later
          </Button>
          <Button
            onClick={applyUpdate}
            disabled={refreshing}
            className="w-full sm:w-auto"
            size="lg"
          >
            {refreshing ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Refreshing…
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh now
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
